/**
 * Deterministic LinkedIn-answer detection for the onboarding chat (LEV-1325).
 *
 * The streamObject LLM kept ignoring bare LinkedIn handles despite extensive
 * prompt instructions, so recognition moved to code (same pattern as
 * [MATERIALS_PARSING] injection): detectLinkedInAnswer inspects the
 * conversation, buildLinkedInAnswerContext injects a [LINKEDIN_ANSWER] block
 * into the system prompt, and applyLinkedInFallback patches
 * result.identity.linkedinUrl before persistence in case the LLM still misses.
 *
 * Client-safe: no Prisma or server-env imports (mirrors schema.ts).
 */

import { normalizeLinkedinProfileUrl } from "@/lib/utils/shared";
import type { OnboardingChatMessage } from "@/lib/onboarding/schema";

export type LinkedInAnswer = {
  /** Canonical https://www.linkedin.com/in/<handle> form. */
  resolvedUrl: string;
  /** How the founder expressed it: full URL, partial linkedin.com URL, or bare handle. */
  kind: "url" | "partial-url" | "handle";
};

/** First linkedin.com URL-ish token in a message (scheme optional). */
const LINKEDIN_URL_TOKEN_RE = /(?:https?:\/\/)?(?:[a-z0-9-]+\.)*linkedin\.com\/\S+/i;

/**
 * Words that pass the handle shape check but are conversational answers, not
 * handles — declines ("skip") and fillers ("ok"). Without this, "skip" after
 * the LinkedIn ask would resolve to linkedin.com/in/skip.
 */
const NON_HANDLE_WORDS = new Set([
  "no", "yes", "ok", "okay", "sure", "skip", "pass", "nope", "yep", "yeah",
  "nah", "none", "later", "thanks", "thank", "done", "next", "hi", "hello",
  "hey", "maybe", "idk", "dunno", "hmm", "umm",
]);

/**
 * Detect whether the LAST user message is the founder's LinkedIn answer.
 *
 * - A linkedin.com URL (full or partial, embedded text ok) counts in any
 *   context.
 * - A bare handle (single token, optionally @-prefixed) counts ONLY when the
 *   assistant message immediately preceding it mentions "linkedin" — outside
 *   that context a lone word could be anything.
 * - Single letters are never handles (they're multiple-choice answers).
 *
 * Returns the canonical resolved URL, or null when the message is not a
 * LinkedIn answer.
 */
export function detectLinkedInAnswer(
  messages: OnboardingChatMessage[],
): LinkedInAnswer | null {
  const last = messages[messages.length - 1];
  if (!last || last.role !== "user") return null;
  const content = last.content.trim();
  if (!content) return null;

  // 1. linkedin.com URL anywhere in the message (full or partial).
  const urlMatch = LINKEDIN_URL_TOKEN_RE.exec(content);
  if (urlMatch) {
    const candidate = urlMatch[0].replace(/[).,!?;:'"\]]+$/, "");
    const resolvedUrl = normalizeLinkedinProfileUrl(candidate);
    if (!resolvedUrl) return null;
    return {
      resolvedUrl,
      kind: /\/in\//i.test(candidate) ? "url" : "partial-url",
    };
  }

  // 2. After a LinkedIn ask, the next user message IS the LinkedIn answer.
  const previousAssistant = [...messages.slice(0, -1)]
    .reverse()
    .find((m) => m.role === "assistant");
  if (!previousAssistant || !/linkedin/i.test(previousAssistant.content)) {
    return null;
  }

  const bare = content.replace(/^@/, "");
  if (bare.length < 2 || NON_HANDLE_WORDS.has(bare.toLowerCase()) || /\s/.test(bare)) {
    return null;
  }
  const resolvedUrl = normalizeLinkedinProfileUrl(bare);
  if (!resolvedUrl) return null;
  return { resolvedUrl, kind: "handle" };
}

/**
 * The most recent user turn that was a LinkedIn answer, by the same rules as
 * detectLinkedInAnswer applied to the history up to that turn. For a turn
 * whose last message is not the answer because Lev replied to it without a
 * model call (IdeaChatInput's local acknowledgement on a program chat).
 *
 * Only a turn that replies to an assistant message mentioning LinkedIn counts,
 * URL or not: the caller saves the result as the founder's identity, and a
 * link sent with any other answer (a cofounder's, a customer's) is not theirs.
 */
export function detectLatestLinkedInAnswer(
  messages: OnboardingChatMessage[],
): LinkedInAnswer | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role !== "user") continue;
    const previous = messages[i - 1];
    if (previous?.role !== "assistant" || !/linkedin/i.test(previous.content)) continue;
    const answer = detectLinkedInAnswer(messages.slice(0, i + 1));
    if (answer) return answer;
  }
  return null;
}

/**
 * - `echo`: today's wording (homepage, program row off).
 * - `quiet`: never write the link back. The echo wording made the model
 *   answer "Perfect, got it: https://www.linkedin.com/in/..." every time.
 * - `earlier`: the answer came in an earlier turn Lev already acknowledged
 *   locally; record it, say nothing about it.
 */
export type LinkedInAnswerMode = "echo" | "quiet" | "earlier";

/**
 * [LINKEDIN_ANSWER] system-prompt block injected by
 * composeOnboardingChatSystemPrompt when detectLinkedInAnswer matches — same
 * pattern as [MATERIALS_PARSING].
 */
export function buildLinkedInAnswerContext(
  resolvedUrl: string,
  mode: LinkedInAnswerMode = "echo",
): string {
  const record = `Set result.identity.linkedinUrl to exactly "${resolvedUrl}" this turn.`;
  const noVerify = "We verify profiles in the background: NEVER ask them to verify or confirm it.";
  if (mode === "earlier") {
    return `[LINKEDIN_ANSWER]
The founder gave their LinkedIn in an earlier message (resolved: ${resolvedUrl}) and you already thanked them for it. ${record} Do not mention it again, and never write the link in your message. ${noVerify}
[/LINKEDIN_ANSWER]`;
  }
  if (mode === "quiet") {
    return `[LINKEDIN_ANSWER]
The founder's last message is their LinkedIn (resolved: ${resolvedUrl}). ${record} Acknowledge it in a few words, and never write the link, the handle or any part of the URL in your message: say "Thanks, got it." and never "Perfect, got it: https://www.linkedin.com/in/...". Then continue as your other instructions say. ${noVerify}
[/LINKEDIN_ANSWER]`;
  }
  return `[LINKEDIN_ANSWER]
The founder's last message is their LinkedIn (resolved: ${resolvedUrl}). Set result.identity.linkedinUrl to exactly "${resolvedUrl}" this turn. Acknowledge it briefly — include the URL — then continue to your next question. We verify profiles in the background: NEVER ask them to verify or confirm it.
[/LINKEDIN_ANSWER]`;
}

/**
 * Belt-and-suspenders: if the streamed result left identity.linkedinUrl empty
 * but the conversation deterministically contains a LinkedIn answer, patch the
 * resolved URL in before the result is persisted. `fromHistory` also reads
 * an answer from an earlier turn (see detectLatestLinkedInAnswer). A null result stays null —
 * fabricating a stub row would overwrite the previously persisted full result.
 */
export function applyLinkedInFallback<T extends Record<string, unknown>>(
  messages: OnboardingChatMessage[],
  result: T | null,
  options: { fromHistory?: boolean } = {},
): T | null {
  if (!result) return null;

  const identity =
    result.identity && typeof result.identity === "object"
      ? (result.identity as Record<string, unknown>)
      : null;
  const existing = identity?.linkedinUrl;
  if (typeof existing === "string" && existing.trim()) return result;

  const detected = options.fromHistory
    ? detectLatestLinkedInAnswer(messages)
    : detectLinkedInAnswer(messages);
  if (!detected) return result;

  return {
    ...result,
    identity: { ...(identity ?? {}), linkedinUrl: detected.resolvedUrl },
  };
}
